/* ==== Flower Point – Delivery Scheduler (cart + minicart) ==== */
/* one file, works in both places, safe to call multiple times  */

(function () {
  const GLOBAL_KEY = '__fpds_init__';
  const CHECKOUT_LISTENER_KEY = '__fpds_checkout_listener__';

  /* ---- utils ---- */
  const pad = (n) => String(n).padStart(2, '0');
  const toAMPM = (h, m) => `${((h + 11) % 12) + 1}:${pad(m)} ${h >= 12 ? 'PM' : 'AM'}`;
  const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const nowDubai = () => {
    try {
      return new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Dubai' }));
    } catch {
      return new Date();
    }
  };
  const parseDate = (val) => {
    if (!val) return null;
    if (val.includes('-')) { const [y, m, d] = val.split('-').map(Number); return new Date(y, m - 1, d); }
    if (val.includes('/')) { const [d, m, y] = val.split('/').map(Number); return new Date(y, m - 1, d); }
    return null;
  };

  /* observe the DOM so the widget initializes when the minicart opens */
  const mountOnEverywhere = () => {
    const mount = () => {
      document.querySelectorAll('#fp-delivery-scheduler').forEach((root) => {
        if (root[GLOBAL_KEY]) return;         // already wired
        initDeliveryScheduler(root);
        root[GLOBAL_KEY] = true;
      });
    };
    document.addEventListener('DOMContentLoaded', mount);
    document.addEventListener('shopify:section:load', mount);
    document.addEventListener('cart:updated', mount);
    document.addEventListener('minicart:open', mount);       // theme custom event (if present)
    new MutationObserver(mount).observe(document.body, { childList: true, subtree: true });
    mount();
  };

  /* ---- MAIN per-widget init ---- */
  function initDeliveryScheduler(root) {
    /* elements */
    const dateInput      = root.querySelector('#fpds_date');
    const windowRadios   = [...root.querySelectorAll('input[name="fpds_window"]')];
    const specificSelect = root.querySelector('#fpds_specific_select');
    const lateNightSelect = root.querySelector('#fpds_latenight_select');
    const attrDate       = root.querySelector('#fpds_attr_date');
    const attrWindow     = root.querySelector('#fpds_attr_window');
    const attrSpecific   = root.querySelector('#fpds_attr_specific');
    const errorBox       = root.querySelector('#fpds_error');

    if (!dateInput || !windowRadios.length) return;

    /* config from snippet dataset */
    const FEE_CUSTOM_ID = String(root.dataset.feeCustom || '').trim(); // +35 (variant id)
    const FEE_LATE_ID   = String(root.dataset.feeLate   || '').trim(); // +70 (variant id)
    const CUTOFF_HR     = parseInt(root.dataset.cutoff || '18', 10);   // 6 PM Dubai
    const DAY_START = 8;
    const DAY_END   = 20;
    const RADIO_VALUE_SPECIFIC = 'Specific time';
    const RADIO_VALUE_LATENIGHT = 'Late Night Custom';

    const WINDOWS = [
      { value: '9 AM – 1 PM',              start: 9,  end: 13 },
      { value: '1 PM – 4 PM',              start: 13, end: 16 },
      { value: '4 PM – 7 PM',              start: 16, end: 19 },
      { value: '7 PM – 8 PM',              start: 19, end: 20 },
      { value: '8 PM – 12 AM (Late Night)',start: 20, end: 24 }
    ];

    /* helpers */
    const isToday = (d) => d && ymd(d) === ymd(nowDubai());

    // 2-hour buffer (rounded up if minutes > 0)
    const bufferHour = () => {
      const t = nowDubai();
      t.setHours(t.getHours() + 2);
      return t.getMinutes() > 0 ? t.getHours() + 1 : t.getHours();
    };

    // set min date (today or tomorrow after cutoff) for both native & any 3rd-party picker
    const applyCutoffMin = () => {
      const t = nowDubai();
      const min = new Date(t);
      if (t.getHours() >= CUTOFF_HR) min.setDate(min.getDate() + 1); // after cutoff -> tomorrow

      const minStr = ymd(min);
      dateInput.setAttribute('min', minStr);
      dateInput.min = minStr;

    // If empty or older than min, force to min (prevents "past date")
      const cur = parseDate(dateInput.value);
      if (!cur || cur < min) {
      dateInput.value = minStr;
      }

    // Nudge any custom datepicker the theme might attach
      dateInput.dispatchEvent(new Event('input',  { bubbles: true }));
      dateInput.dispatchEvent(new Event('change', { bubbles: true }));
    };

    // Mobile browser validation - works even when min attribute is ignored
    // Normalize dates to midnight for proper day-level comparison
    const validateDateForMobile = (dateValue) => {
      if (!dateValue) return false;
      
      const t = nowDubai();
      let min = new Date(t);
      min.setHours(0, 0, 0, 0); // Normalize to midnight
      if (t.getHours() >= CUTOFF_HR) min.setDate(min.getDate() + 1);
      
      const selectedDate = parseDate(dateValue);
      if (!selectedDate) return false;
      // Normalize selected date to midnight for comparison
      const selectedNormalized = new Date(selectedDate);
      selectedNormalized.setHours(0, 0, 0, 0);
      return selectedNormalized >= min;
    };

    const formPost = async (url, paramsObj) => {
      const params = new URLSearchParams();
      Object.entries(paramsObj).forEach(([k, v]) => params.set(k, String(v)));
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          'Accept': 'application/json'
        },
        body: params.toString()
      });
      
      // Check if the response is ok
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Cart update failed:', errorText);
        throw new Error(`Cart update failed: ${response.status} ${response.statusText}`);
      }
      
      // Try to parse as JSON, but don't fail if it's not JSON
      try {
        return await response.json();
      } catch {
        return response;
      }
    };

    const saveAttributes = async () => {
      try {
        // Check if cart has items first
        const cartCheck = await fetch('/cart.js', { credentials: 'same-origin' });
        const cartData = await cartCheck.json();
        if (!cartData || cartData.item_count === 0) {
          // Can't update attributes on empty cart
          return;
        }
        
        // Only send attributes that have values
        const attrs = {};
        if (attrDate.value) attrs['attributes[Delivery Date]'] = attrDate.value;
        if (attrWindow.value) attrs['attributes[Delivery Window]'] = attrWindow.value;
        if (attrSpecific.value) attrs['attributes[Specific Time]'] = attrSpecific.value;
        
        // Only update if we have at least one attribute
        if (Object.keys(attrs).length > 0) {
          await formPost('/cart/update.js', attrs);
        }
      } catch (error) {
        console.error('Failed to save delivery attributes:', error);
        // Don't throw - allow form to continue even if attributes fail
      }
    };

    const setFee = async (mode) => {
      try {
        // First, get current cart to check which items exist
        const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
        const cart = await cartRes.json();
        const existingVariantIds = new Set(cart.items.map(item => String(item.variant_id)));
        
        const updates = {};
        const adds = [];
        
        if (mode === 'custom' && FEE_CUSTOM_ID) {
          if (existingVariantIds.has(FEE_CUSTOM_ID)) {
            updates[`updates[${FEE_CUSTOM_ID}]`] = 1;
          } else {
            adds.push({ id: FEE_CUSTOM_ID, quantity: 1 });
          }
          // Remove late night fee if it exists
          if (FEE_LATE_ID && existingVariantIds.has(FEE_LATE_ID)) {
            updates[`updates[${FEE_LATE_ID}]`] = 0;
          }
        } else if (mode === 'late' && FEE_LATE_ID) {
          if (existingVariantIds.has(FEE_LATE_ID)) {
            updates[`updates[${FEE_LATE_ID}]`] = 1;
          } else {
            adds.push({ id: FEE_LATE_ID, quantity: 1 });
          }
          // Remove custom fee if it exists
          if (FEE_CUSTOM_ID && existingVariantIds.has(FEE_CUSTOM_ID)) {
            updates[`updates[${FEE_CUSTOM_ID}]`] = 0;
          }
        } else {
          // Clear both fees
          if (FEE_CUSTOM_ID && existingVariantIds.has(FEE_CUSTOM_ID)) {
            updates[`updates[${FEE_CUSTOM_ID}]`] = 0;
          }
          if (FEE_LATE_ID && existingVariantIds.has(FEE_LATE_ID)) {
            updates[`updates[${FEE_LATE_ID}]`] = 0;
          }
        }
        
        // Update existing items
        if (Object.keys(updates).length) {
          const updateRes = await formPost('/cart/update.js', updates);
          if (!updateRes || (updateRes.errors && updateRes.errors.length > 0)) {
            throw new Error('Cart update failed: ' + (updateRes.errors ? updateRes.errors.join(', ') : 'Unknown error'));
          }
        }
        
        // Add new items
        for (const item of adds) {
          if (!item.id || !item.quantity) continue; // Skip invalid items
          const addRes = await fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({ id: item.id, quantity: item.quantity })
          });
          const addData = await addRes.json();
          if (!addRes.ok || (addData.status && addData.status !== 200)) {
            throw new Error('Failed to add item to cart: ' + (addData.description || 'Unknown error'));
          }
        }
      } catch (error) {
        console.error('Failed to set delivery fee:', error);
        // Don't throw - allow form to continue even if fee update fails
      }
    };

    const persistThenFeeThenUpdate = async (mode) => {
      try {
        // Save attributes first
        await saveAttributes();
        // Then set fees
        await setFee(mode);
        // Small delay to ensure cart is updated
        await new Promise(resolve => setTimeout(resolve, 100));
        // Trigger cart update event instead of reloading page
        // This allows the theme to update cart UI without full page reload
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
        // Also trigger a custom event for cart section refresh if needed
        const cartForm = document.getElementById('cart');
        if (cartForm) {
          cartForm.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } catch (error) {
        console.error('Failed to persist delivery settings:', error);
        // Still trigger events even if update fails
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
      }
    };

    const syncHidden = () => {
      attrDate.value = dateInput.value || '';
      const checked = root.querySelector('input[name="fpds_window"]:checked');
      attrWindow.value  = checked ? checked.value : '';
      if (checked) {
        if (checked.value === RADIO_VALUE_SPECIFIC) {
          attrSpecific.value = specificSelect.value || '';
        } else if (checked.value === RADIO_VALUE_LATENIGHT) {
          attrSpecific.value = lateNightSelect.value || '';
        } else {
          attrSpecific.value = '';
        }
      } else {
        attrSpecific.value = '';
      }
    };

    const buildSpecificTimes = () => {
      const d = parseDate(dateInput.value);
      specificSelect.innerHTML = '<option value="">— choose specific time —</option>';
      if (!d) return 0;

      const today = isToday(d);
      const bh = bufferHour();
      const now = nowDubai();
      let count = 0;

      for (let h = DAY_START; h <= DAY_END; h++) {
        for (const m of [0, 30]) {
          if (h === DAY_END && m > 0) continue; // stop at 20:00
          let ok = true;
          if (today) {
            if (h < bh || (h === bh && now.getMinutes() > 0 && m === 0)) ok = false;
          }
          if (ok) {
            specificSelect.add(new Option(toAMPM(h, m), `${pad(h)}:${pad(m)}`));
            count++;
          }
        }
      }
      return count;
    };

    const buildLateNightTimes = () => {
      const d = parseDate(dateInput.value);
      lateNightSelect.innerHTML = '<option value="">— choose late night time —</option>';
      if (!d) return 0;

      const today = isToday(d);
      const bh = bufferHour();
      const now = nowDubai();
      let count = 0;

      for (let h = 20; h <= 24; h++) { // 20 = 8 PM, 24 = 12 AM (midnight)
        for (const m of [0, 30]) {
          // Skip 12:30 AM since late night ends at 12:00 AM
          if (h === 24 && m === 30) continue;
          
          // Include all slots: 8:00, 8:30, 9:00, 9:30, ..., 11:00, 11:30, 12:00
          let ok = true;
          if (today) {
            // For late night, we need to check if current time + buffer is before the slot
            // Late night starts at 8 PM (20:00), so if it's same day, check buffer
            if (h < bh || (h === bh && now.getMinutes() > 0 && m === 0)) ok = false;
          }
          if (ok) {
            // Handle midnight (24:00) as 00:00 for display
            const displayHour = h === 24 ? 0 : h;
            lateNightSelect.add(new Option(toAMPM(displayHour, m), `${pad(displayHour)}:${pad(m)}`));
            count++;
          }
        }
      }
      return count;
    };

    const refreshWindows = (specCount = 0, lateNightCount = 0) => {
      const d = parseDate(dateInput.value);
      const today = d && isToday(d);
      const bh = bufferHour();

      windowRadios.forEach((r) => {
        const isSpec = r.value === RADIO_VALUE_SPECIFIC;
        const isLateNight = r.value === RADIO_VALUE_LATENIGHT;
        const def = WINDOWS.find((w) => w.value === r.value);
        let dis = false;

        if (!d) dis = true;
        else if (isSpec) dis = specCount === 0;
        else if (isLateNight) dis = lateNightCount === 0;
        else if (today) {
          // For same-day delivery: disable if window END time is before or equal to buffer hour
          // Example: if it's 3pm (bh=17/5pm), 4-7pm slot (end=19) should be available (19 > 17)
          // But 1-4pm slot (end=16) should be disabled (16 <= 17)
          dis = def && (def.end <= bh);
        }

        r.disabled = dis;
        r.closest('label').classList.toggle('fpds-disabled', dis);
        if (dis && r.checked) r.checked = false;
      });

      const checked = root.querySelector('input[name="fpds_window"]:checked');
      specificSelect.disabled = !(checked && checked.value === RADIO_VALUE_SPECIFIC);
      lateNightSelect.disabled = !(checked && checked.value === RADIO_VALUE_LATENIGHT);
    };

    /* hydrate from cart */
    const hydrateFromCart = async () => {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      const c = await res.json();
      const a = c.attributes || {};

      // pick min date before applying saved value
      applyCutoffMin();

      // Apply saved date only if it's valid (not in the past) - with mobile validation
      if (a['Delivery Date']) {
        const savedDate = parseDate(a['Delivery Date']);
        const minDate = parseDate(dateInput.min);
        // Use mobile validation to ensure saved date is still valid
        if (savedDate && (!minDate || savedDate >= minDate) && validateDateForMobile(a['Delivery Date'])) {
          dateInput.value = a['Delivery Date'];
        }
      }

      const count = buildSpecificTimes();
      const lateNightCount = buildLateNightTimes();
      refreshWindows(count, lateNightCount);

      if (a['Delivery Window']) {
        const r = windowRadios.find((x) => x.value === a['Delivery Window']);
        if (r && !r.disabled) r.checked = true;
        specificSelect.disabled = a['Delivery Window'] !== RADIO_VALUE_SPECIFIC;
        lateNightSelect.disabled = a['Delivery Window'] !== RADIO_VALUE_LATENIGHT;
      }

      if (a['Specific Time'] && !specificSelect.disabled) {
        const opt = [...specificSelect.options].find((o) => o.value === a['Specific Time']);
        if (opt) specificSelect.value = a['Specific Time'];
      }

      if (a['Specific Time'] && !lateNightSelect.disabled) {
        const opt = [...lateNightSelect.options].find((o) => o.value === a['Specific Time']);
        if (opt) lateNightSelect.value = a['Specific Time'];
      }

      syncHidden();
    };

    /* events */
    dateInput.addEventListener('change', async () => {
      // Mobile browser validation - works even when min attribute is ignored
      if (!validateDateForMobile(dateInput.value)) {
        // Force to minimum valid date
        const t = nowDubai();
        const min = new Date(t);
        if (t.getHours() >= CUTOFF_HR) min.setDate(min.getDate() + 1);
        dateInput.value = ymd(min);
        
        // Show error message for mobile users
        errorBox.textContent = 'Please select a valid delivery date. Past dates are not available.';
        errorBox.hidden = false;
        setTimeout(() => { errorBox.hidden = true; }, 3000);
      }

      const n = buildSpecificTimes();
      const lateNightCount = buildLateNightTimes();
      refreshWindows(n, lateNightCount);
      syncHidden();
      await saveAttributes();
      errorBox.hidden = true;
    }, true);

    // Additional input event for real-time mobile validation
    dateInput.addEventListener('input', () => {
      // Real-time validation for mobile browsers
      if (dateInput.value && !validateDateForMobile(dateInput.value)) {
        // Don't change the value immediately, just show warning
        errorBox.textContent = 'Please select a valid delivery date. Past dates are not available.';
        errorBox.hidden = false;
      } else {
        errorBox.hidden = true;
      }
    }, true);

    windowRadios.forEach((r) => r.addEventListener('change', async () => {
      const val = r.value;
      const isSpec = val === RADIO_VALUE_SPECIFIC;
      const isLateNight = val === RADIO_VALUE_LATENIGHT;

      specificSelect.disabled = !isSpec;
      lateNightSelect.disabled = !isLateNight;
      syncHidden();
      await saveAttributes();

      if (isLateNight) {
        buildLateNightTimes();
        await persistThenFeeThenUpdate('late');
        return;
      }
      if (isSpec) { 
        await persistThenFeeThenUpdate('custom'); 
        return; 
      }

      await setFee(null); // free window
    }, true));

    specificSelect.addEventListener('change', async () => {
      syncHidden();
      await saveAttributes();
    }, true);
    
    lateNightSelect.addEventListener('change', async () => {
      syncHidden();
      await saveAttributes();
    }, true);

    // shield from theme handlers
    [dateInput, specificSelect, lateNightSelect, ...windowRadios].forEach((el) => {
      if (!el) return; // guard against null
      ['change','click','input'].forEach(evt =>
        el.addEventListener(evt, (e) => { 
          e.stopPropagation(); 
          e.stopImmediatePropagation?.(); 
        }, true)
      );
    });

    // Attach checkout button listener only once, and only from cart page widget
    const checkoutBtn = document.getElementById('checkout');
    if (checkoutBtn && !checkoutBtn[CHECKOUT_LISTENER_KEY]) {
      // Only attach if this widget is on the cart page (not minicart)
      const isCartPage = root.closest('#main-cart-items') !== null;
      if (isCartPage) {
        checkoutBtn[CHECKOUT_LISTENER_KEY] = true;
        checkoutBtn.addEventListener('click', async (e) => {
          // Get fresh element references from the cart page delivery scheduler
          const cartScheduler = document.querySelector('#main-cart-items #fp-delivery-scheduler');
          if (!cartScheduler) {
            // If scheduler not found, allow normal form submission
            return;
          }

          // Get fresh references to all elements
          const freshDateInput = cartScheduler.querySelector('#fpds_date');
          const freshAttrDate = cartScheduler.querySelector('#fpds_attr_date');
          const freshAttrWindow = cartScheduler.querySelector('#fpds_attr_window');
          const freshAttrSpecific = cartScheduler.querySelector('#fpds_attr_specific');
          const freshErrorBox = cartScheduler.querySelector('#fpds_error');
          const freshChecked = cartScheduler.querySelector('input[name="fpds_window"]:checked');
          const freshSpecificSelect = cartScheduler.querySelector('#fpds_specific_select');
          const freshLateNightSelect = cartScheduler.querySelector('#fpds_latenight_select');

          if (!freshDateInput || !freshAttrDate || !freshAttrWindow || !freshErrorBox) {
            // If elements not found, allow normal form submission
            return;
          }

          // CRITICAL: Sync values from visible inputs to hidden inputs
          // Read date value - HTML5 date inputs use YYYY-MM-DD format
          let dateValue = freshDateInput.value || '';
          
          // Fallback: try reading from various sources if value is empty
          if (!dateValue) {
            dateValue = freshDateInput.getAttribute('value') || '';
          }
          if (!dateValue) {
            dateValue = freshAttrDate.value || '';
          }
          
          // Read window value from checked radio button
          let windowValue = '';
          if (freshChecked) {
            windowValue = freshChecked.value || '';
          }
          // Fallback: try reading from hidden input
          if (!windowValue) {
            windowValue = freshAttrWindow.value || '';
          }
          
          // Now sync to hidden inputs (these are what get submitted)
          freshAttrDate.value = dateValue;
          freshAttrWindow.value = windowValue;
          
          // Handle specific time selects
          if (freshChecked && windowValue) {
            const checkedValue = freshChecked.value;
            if (checkedValue === RADIO_VALUE_SPECIFIC) {
              freshAttrSpecific.value = freshSpecificSelect ? (freshSpecificSelect.value || '') : '';
            } else if (checkedValue === RADIO_VALUE_LATENIGHT) {
              freshAttrSpecific.value = freshLateNightSelect ? (freshLateNightSelect.value || '') : '';
            } else {
              freshAttrSpecific.value = '';
            }
          } else {
            freshAttrSpecific.value = '';
          }

          // Get CUTOFF_HR from the scheduler dataset (not from closure)
          const cutoffHr = parseInt(cartScheduler.dataset.cutoff || '18', 10);
          
          // Inline validation function using correct CUTOFF_HR
          // Normalize dates to midnight for proper day-level comparison
          const validateDate = (dateValue) => {
            if (!dateValue) return false;
            const t = nowDubai();
            let min = new Date(t);
            min.setHours(0, 0, 0, 0); // Normalize to midnight
            if (t.getHours() >= cutoffHr) {
              min.setDate(min.getDate() + 1);
            }
            const selectedDate = parseDate(dateValue);
            if (!selectedDate) return false;
            // Normalize selected date to midnight for comparison
            const selectedNormalized = new Date(selectedDate);
            selectedNormalized.setHours(0, 0, 0, 0);
            return selectedNormalized >= min;
          };

          // Validate
          const needSpecific = windowValue === RADIO_VALUE_SPECIFIC;
          const needLateNight = windowValue === RADIO_VALUE_LATENIGHT;
          const minDate = parseDate(freshDateInput.min);
          const curDate = parseDate(dateValue);

          // Validation checks
          if (!dateValue) {
            e.preventDefault();
            e.stopPropagation();
            freshErrorBox.textContent = 'Please choose a delivery date.';
            freshErrorBox.hidden = false;
            return;
          }
          
          if (!windowValue) {
            e.preventDefault();
            e.stopPropagation();
            freshErrorBox.textContent = 'Please choose a delivery time window.';
            freshErrorBox.hidden = false;
            return;
          }
          
          if (needSpecific && !freshAttrSpecific.value) {
            e.preventDefault();
            e.stopPropagation();
            freshErrorBox.textContent = 'Please choose a specific time.';
            freshErrorBox.hidden = false;
            return;
          }
          
          if (needLateNight && !freshAttrSpecific.value) {
            e.preventDefault();
            e.stopPropagation();
            freshErrorBox.textContent = 'Please choose a late night time.';
            freshErrorBox.hidden = false;
            return;
          }
          
          // Normalize dates to midnight for proper comparison
          if (minDate && curDate) {
            const minNormalized = new Date(minDate);
            minNormalized.setHours(0, 0, 0, 0);
            const curNormalized = new Date(curDate);
            curNormalized.setHours(0, 0, 0, 0);
            if (curNormalized < minNormalized) {
              e.preventDefault();
              e.stopPropagation();
              freshErrorBox.textContent = 'Please choose a valid delivery date. Past dates are not available.';
              freshErrorBox.hidden = false;
              return;
            }
          }
          
          if (!validateDate(dateValue)) {
            e.preventDefault();
            e.stopPropagation();
            freshErrorBox.textContent = 'Please choose a valid delivery date.';
            freshErrorBox.hidden = false;
            return;
          }

          // Get fee variant IDs from the scheduler
          const feeCustomId = String(cartScheduler.dataset.feeCustom || '').trim();
          const feeLateId = String(cartScheduler.dataset.feeLate || '').trim();

          // Ensure attributes are saved before proceeding
          try {
            // Check cart first
            const cartCheck = await fetch('/cart.js', { credentials: 'same-origin' });
            const cartData = await cartCheck.json();
            if (!cartData || cartData.item_count === 0) {
              e.preventDefault();
              e.stopPropagation();
              freshErrorBox.textContent = 'Your cart is empty. Please add items to cart.';
              freshErrorBox.hidden = false;
              return;
            }
            
            // Only send attributes that have values
            const attrs = {};
            if (freshAttrDate.value) attrs['attributes[Delivery Date]'] = freshAttrDate.value;
            if (freshAttrWindow.value) attrs['attributes[Delivery Window]'] = freshAttrWindow.value;
            if (freshAttrSpecific.value) attrs['attributes[Specific Time]'] = freshAttrSpecific.value;
            
            // Update attributes if we have any
            if (Object.keys(attrs).length > 0) {
              await formPost('/cart/update.js', attrs);
            }
          } catch (error) {
            console.error('Failed to save delivery attributes before checkout:', error);
            e.preventDefault();
            e.stopPropagation();
            freshErrorBox.textContent = 'Failed to save delivery details. Please try again.';
            freshErrorBox.hidden = false;
            return;
          }

          // Set fees if needed - check cart first to see what exists
          try {
            const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
            const cart = await cartRes.json();
            const existingVariantIds = new Set(cart.items.map(item => String(item.variant_id)));
            
            const updates = {};
            const adds = [];
            
            if (needLateNight && feeLateId) {
              if (existingVariantIds.has(feeLateId)) {
                updates[`updates[${feeLateId}]`] = 1;
              } else {
                adds.push({ id: feeLateId, quantity: 1 });
              }
              // Remove custom fee if it exists
              if (feeCustomId && existingVariantIds.has(feeCustomId)) {
                updates[`updates[${feeCustomId}]`] = 0;
              }
            } else if (needSpecific && freshAttrSpecific.value && feeCustomId) {
              if (existingVariantIds.has(feeCustomId)) {
                updates[`updates[${feeCustomId}]`] = 1;
              } else {
                adds.push({ id: feeCustomId, quantity: 1 });
              }
              // Remove late night fee if it exists
              if (feeLateId && existingVariantIds.has(feeLateId)) {
                updates[`updates[${feeLateId}]`] = 0;
              }
            } else {
              // Clear fees - only update if they exist in cart
              if (feeCustomId && existingVariantIds.has(feeCustomId)) {
                updates[`updates[${feeCustomId}]`] = 0;
              }
              if (feeLateId && existingVariantIds.has(feeLateId)) {
                updates[`updates[${feeLateId}]`] = 0;
              }
            }
            
            // Update existing items
            if (Object.keys(updates).length) {
              const updateRes = await formPost('/cart/update.js', updates);
              if (!updateRes || (updateRes.errors && updateRes.errors.length > 0)) {
                throw new Error('Cart update failed: ' + (updateRes.errors ? updateRes.errors.join(', ') : 'Unknown error'));
              }
            }
            
            // Add new items
            for (const item of adds) {
              if (!item.id || !item.quantity) continue; // Skip invalid items
              const addRes = await fetch('/cart/add.js', {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                  'Content-Type': 'application/json',
                  'Accept': 'application/json'
                },
                body: JSON.stringify({ id: item.id, quantity: item.quantity })
              });
              const addData = await addRes.json();
              if (!addRes.ok || (addData.status && addData.status !== 200)) {
                throw new Error('Failed to add item to cart: ' + (addData.description || 'Unknown error'));
              }
            }
          } catch (error) {
            console.error('Failed to update delivery fees before checkout:', error);
            // Don't block checkout if fee update fails - attributes are more important
          }

          // Small delay to ensure cart is fully updated
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Redirect to checkout
          window.location.href = '/checkout';
        }, true);
      }
    }

    // first hydrate
    hydrateFromCart();

  }

  /* expose for inline calls if needed */
  window.initDeliveryScheduler = (root) => initDeliveryScheduler(root);

  /* start watching the DOM so minicart works without reload */
  mountOnEverywhere();
})();